import { useState, useEffect, useMemo } from 'react';
import { PmCard, PmColumnPicker, PmHeadCell, useTableSortFilter, handleInlineEditKeyDown, type SortFilterColumn } from '../../../components/pm';
import {
  Box, Typography, IconButton, CircularProgress, Stack,
  Table, TableHead, TableRow, TableCell, TableBody, TextField,
  Select, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useProjectTeam, useCreateTeamMember, useUpdateTeamMember, useDeleteTeamMember } from '../../../api/hooks/useProjectTeam';
import { useProject } from '../../../api/hooks/useProjects';
import { useSyncMaturityFromCount } from '../../../api/hooks/useReifegrad';
import { useAutosave } from '../../../hooks/useAutosave';
import { AutosaveIndicator } from '../../../components/common/AutosaveIndicator';
import { PdfExportButton } from '../../../components/common/PdfExportButton';
import { buildTeamReport } from '../reports/teamReport';
import type { ReportMeta } from '../../../lib/pdf/reportModel';
import { TeamOrgCanvas } from '../components/TeamOrgCanvas';
import { useNotifications } from '../../../context/NotificationContext';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { apiErrorMessage } from '../../../lib/apiErrorMessage';

interface TeamMember {
  id: string;
  name: string;
  rolle: string | null;
  funktion: string | null;
  email: string | null;
  /** PHQ-70: freies Telefonfeld (höchstens 50 Zeichen, Ziffern, Leerzeichen und + - ( ) .). */
  telefon?: string | null;
}

interface MemberFields {
  name: string;
  funktion: string;
  rolle: string;
  email: string;
  telefon: string;
}

const TELEFON_MAX = 50;
const TELEFON_RE = /^[0-9 +\-().]*$/;

/** Clientseitige Formatprüfung wie pm-api; leer ist erlaubt. */
export function isValidTelefon(v: string): boolean {
  return v.length <= TELEFON_MAX && TELEFON_RE.test(v);
}

const ROLLEN = [
  'Sponsor',
  'Auftraggeber',
  'Lenkungskreis',
  'Projektleiter',
  'Teilprojektleiter',
  'Teammitglied',
  'Fachexperte',
  'Extern',
] as const;

/** Altbestand: Rolle hieß früher „Externer“. */
const normRolle = (r: string | null): string | null => (r === 'Externer' ? 'Extern' : r);

/** Wandelt die Formularwerte in das API-Payload (leere Strings -> null). */
function toPayload(f: MemberFields): Record<string, unknown> {
  return {
    name: f.name.trim(),
    funktion: f.funktion.trim() || null,
    rolle: f.rolle || null,
    email: f.email.trim() || null,
    telefon: f.telefon.trim() || null,
  };
}

export function TeamTab({ projectId, embedded = false }: { projectId: string; embedded?: boolean }) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();

  const { data, isLoading } = useProjectTeam(projectId);
  const { data: projectResp } = useProject(projectId);
  const projectName =
    (projectResp as unknown as { data?: { name?: string } } | undefined)?.data?.name ?? 'Projekt';
  const createMut = useCreateTeamMember(projectId);
  const updateMut = useUpdateTeamMember(projectId);
  const deleteMut = useDeleteTeamMember(projectId);

  const items = ((data as unknown as { data?: TeamMember[] } | undefined)?.data ?? []);

  // Reifegrad Schritt 6 nach Anzahl der Mitglieder.
  useSyncMaturityFromCount(projectId, 6, isLoading ? -1 : items.length);

  const [deleteId, setDeleteId] = useState<string | null>(null);

  const columns = useMemo<SortFilterColumn<TeamMember>[]>(() => [
    {
      key: 'name', label: t('fields.name', 'Name'), hideable: false,
      filter: { type: 'text' }, value: (r) => r.name,
    },
    {
      key: 'funktion', label: t('team.funktion', 'Funktion'), hideable: false,
      filter: { type: 'text' }, value: (r) => r.funktion ?? '',
    },
    {
      key: 'rolle', label: t('team.rolle', 'Rolle'), hideable: false,
      filter: { type: 'enum', options: ROLLEN.map((r) => ({ value: r, label: r })) },
      value: (r) => r.rolle ?? '',
    },
    {
      key: 'email', label: t('team.email', 'E-Mail'), hideable: false,
      filter: { type: 'text' }, value: (r) => r.email ?? '',
    },
    {
      key: 'telefon', label: t('team.telefon', 'Telefon'),
      filter: { type: 'text' }, value: (r) => r.telefon ?? '',
    },
  ], [t]);

  const sf = useTableSortFilter(items, columns, { tableId: 'team' });

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await deleteMut.mutateAsync(deleteId); notify(tc('status.deleted', 'Gelöscht'), 'success'); }
    catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
    finally { setDeleteId(null); }
  };

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  const headCellSx = { color: 'var(--pm-fg-subtle)', fontWeight: 600 };

  const pdfMeta: ReportMeta = { projectName, pageTitle: t('team.title', 'Projekt-Team') };
  const buildReport = () =>
    buildTeamReport({
      team: items.map((m) => ({ name: m.name, rolle: m.rolle, funktion: m.funktion, email: m.email })),
    });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">{t('team.title', 'Projekt-Team')}</Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <PmColumnPicker columns={sf.hideableColumns} isVisible={sf.isVisible} onToggle={sf.toggleColumn} />
          {!embedded && <PdfExportButton meta={pdfMeta} build={buildReport} />}
        </Stack>
      </Stack>

      <PmCard flush>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--pm-bg-muted)' }}>
          <Typography variant="subtitle2" fontWeight={600}>{t('team.listTitle', 'Team-Liste')}</Typography>
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
            {t('team.inlineHint', 'Oben direkt eintragen und Enter drücken — Name genügt. Bestehende Zeilen sind direkt editierbar.')}
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              {sf.visibleColumns.map((col) => (
                <PmHeadCell
                  key={col.key}
                  label={col.label}
                  align={col.align}
                  filter={col.filter}
                  sortActive={sf.sortKey === col.key}
                  sortDir={sf.sortDir}
                  onToggleSort={() => sf.toggleSort(col.key)}
                  filterValue={sf.filters[col.key]}
                  onSetFilter={(v) => sf.setFilter(col.key, v)}
                  onClearFilter={() => sf.clearFilter(col.key)}
                  headSx={headCellSx}
                  minWidth={col.key === 'name' ? 200 : col.key === 'funktion' ? 140 : col.key === 'rolle' ? 150 : 180}
                />
              ))}
              <TableCell sx={headCellSx} align="right">{tc('actions.actions', 'Aktionen')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <AddMemberRow
              isVisible={sf.isVisible}
              onCreate={async (payload) => { await createMut.mutateAsync(payload); }}
            />
            {sf.rows.map((m) => (
              <MemberRow
                key={m.id}
                member={m}
                isVisible={sf.isVisible}
                onSave={async (payload) => { await updateMut.mutateAsync({ id: m.id, data: payload }); }}
                onDelete={() => setDeleteId(m.id)}
              />
            ))}
            {sf.rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={sf.visibleColumns.length + 1}>
                  <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
                    {sf.activeFilterCount > 0 || sf.sortKey
                      ? t('team.filterEmpty', 'Keine Treffer für die aktuelle Filter-Kombination.')
                      : t('team.empty', 'Noch kein Team erfasst — oben in der ersten Zeile eintragen.')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </PmCard>

      {/* Organigramm */}
      <PmCard sx={{ mt: 2 }}>
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>{t('team.orgChart', 'Organigramm')}</Typography>
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
            {t('team.orgChartHintAuto', 'Automatische Anordnung: Zeile nach Rolle, Reihenfolge nach Hinzufügen. Externe und Fachexperten sind als fremde Firma abgesetzt.')}
          </Typography>
        </Box>
        {items.length === 0 ? (
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
            {t('team.orgChartEmpty', 'Mitglieder anlegen, damit das Organigramm aufgebaut wird.')}
          </Typography>
        ) : (
          <TeamOrgCanvas
            members={items.map((m) => ({ id: m.id, name: m.name, rolle: m.rolle, funktion: m.funktion, email: m.email }))}
          />
        )}
      </PmCard>

      <ConfirmDialog
        open={!!deleteId}
        title={t('team.deleteTitle', 'Mitglied entfernen?')}
        message={t('team.deleteConfirm', 'Das Mitglied wird aus dem Team entfernt.')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </Box>
  );
}

/**
 * Inline-Anlegen direkt in der ersten Tabellenzeile. Name = Pflicht; Enter (in
 * einem beliebigen Feld) erzeugt den Eintrag und leert die Zeile. Dient als
 * Vorlage fuer das spaetere globale Inline-Anlegen.
 */
function AddMemberRow({ onCreate, isVisible }: { isVisible: (k: string) => boolean; onCreate: (payload: Record<string, unknown>) => Promise<void> }) {
  const { t } = useTranslation('projects');
  const EMPTY: MemberFields = { name: '', funktion: '', rolle: 'Teammitglied', email: '', telefon: '' };
  const [form, setForm] = useState<MemberFields>(EMPTY);
  const [busy, setBusy] = useState(false);

  const telefonOk = isValidTelefon(form.telefon);
  const canCreate = form.name.trim().length > 0 && telefonOk;

  const submit = async () => {
    if (!canCreate || busy) return;
    setBusy(true);
    try {
      await onCreate(toPayload(form));
      setForm(EMPTY);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); void submit(); }
  };

  return (
    <TableRow sx={{ bgcolor: 'var(--pm-bg-subtle)' }}>
      <TableCell>
        <TextField
          variant="standard" fullWidth autoFocus
          placeholder={t('team.namePlaceholder', 'Name eingeben…')}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          onKeyDown={onKeyDown}
        />
      </TableCell>
      <TableCell>
        <TextField
          variant="standard" fullWidth
          placeholder={t('team.funktion', 'Funktion')}
          value={form.funktion}
          onChange={(e) => setForm({ ...form, funktion: e.target.value })}
          onKeyDown={onKeyDown}
        />
      </TableCell>
      <TableCell>
        <Select
          variant="standard" fullWidth
          value={form.rolle}
          onChange={(e) => setForm({ ...form, rolle: e.target.value })}
        >
          {ROLLEN.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
        </Select>
      </TableCell>
      <TableCell>
        <TextField
          variant="standard" fullWidth type="email"
          placeholder={t('team.email', 'E-Mail')}
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          onKeyDown={onKeyDown}
        />
      </TableCell>
      {isVisible('telefon') && (
        <TableCell>
          <TextField
            variant="standard" fullWidth type="tel"
            placeholder={t('team.telefon', 'Telefon')}
            value={form.telefon}
            error={!telefonOk}
            helperText={!telefonOk ? t('team.telefonInvalid', 'Nur Ziffern, Leerzeichen und + - ( ) . erlaubt, höchstens 50 Zeichen.') : undefined}
            onChange={(e) => setForm({ ...form, telefon: e.target.value })}
            onKeyDown={onKeyDown}
          />
        </TableCell>
      )}
      <TableCell align="right">
        <IconButton
          color="primary"
          onClick={() => void submit()}
          disabled={!canCreate || busy}
          aria-label={t('team.add', 'Mitglied hinzufügen')}
        >
          <AddIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

/** Bestehende Zeile — direkt editierbar mit Autosave (Enter speichert sofort). */
function MemberRow({
  member, onSave, onDelete, isVisible,
}: {
  isVisible: (k: string) => boolean;
  member: TeamMember;
  onSave: (payload: Record<string, unknown>) => Promise<void>;
  onDelete: () => void;
}) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');

  const [form, setForm] = useState<MemberFields>({
    name: member.name,
    funktion: member.funktion ?? '',
    rolle: normRolle(member.rolle) ?? 'Teammitglied',
    email: member.email ?? '',
    telefon: member.telefon ?? '',
  });

  const autosave = useAutosave<MemberFields>({
    canSave: (f) => f.name.trim().length > 0 && isValidTelefon(f.telefon),
    onSave: async (f) => { await onSave(toPayload(f)); },
  });

  useEffect(() => {
    if (autosave.isDirty) return;
    setForm({
      name: member.name,
      funktion: member.funktion ?? '',
      rolle: normRolle(member.rolle) ?? 'Teammitglied',
      email: member.email ?? '',
      telefon: member.telefon ?? '',
    });
  }, [autosave.isDirty, member.name, member.funktion, member.rolle, member.email, member.telefon]);

  const update = (patch: Partial<MemberFields>) => {
    const next = { ...form, ...patch };
    setForm(next);
    autosave.schedule(next);
  };

  const onKeyDown = (e: React.KeyboardEvent) => handleInlineEditKeyDown(e, autosave.flush);

  return (
    <TableRow hover>
      <TableCell>
        <TextField variant="standard" fullWidth value={form.name}
          onChange={(e) => update({ name: e.target.value })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell>
        <TextField variant="standard" fullWidth value={form.funktion}
          placeholder={t('team.funktion', 'Funktion')}
          onChange={(e) => update({ funktion: e.target.value })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell>
        <Select variant="standard" fullWidth value={form.rolle}
          onChange={(e) => update({ rolle: e.target.value })}>
          {ROLLEN.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
        </Select>
      </TableCell>
      <TableCell>
        <TextField variant="standard" fullWidth type="email" value={form.email}
          placeholder={t('team.email', 'E-Mail')}
          onChange={(e) => update({ email: e.target.value })} onKeyDown={onKeyDown} />
      </TableCell>
      {isVisible('telefon') && (
        <TableCell>
          <TextField variant="standard" fullWidth type="tel" value={form.telefon}
            placeholder={t('team.telefon', 'Telefon')}
            error={!isValidTelefon(form.telefon)}
            helperText={!isValidTelefon(form.telefon) ? t('team.telefonInvalid', 'Nur Ziffern, Leerzeichen und + - ( ) . erlaubt, höchstens 50 Zeichen.') : undefined}
            onChange={(e) => update({ telefon: e.target.value })} onKeyDown={onKeyDown} />
        </TableCell>
      )}
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
          <AutosaveIndicator status={autosave.status} onRetry={autosave.flush} compact />
          <IconButton size="small" color="error" onClick={onDelete} aria-label={tc('actions.delete', 'Löschen')}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
}
